import React, { useState } from 'react';
import { TrendingUp, BarChart2, FileText, PhoneCall, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Main Page Container
const TaxPlanningPage = ({ children }) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#E8EEF6]">
      {children}
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  const dummy = "https://jamaappprod.s3.ap-south-1.amazonaws.com/Website/maxwealthweb/Web-Images+compressed/Tax.png";
  const navigate = useNavigate();

  const handleBackClick = () => {
    // Navigate back to main page
    navigate('/wealth-services/tax-planning');

    // Use a small timeout to ensure navigation completes before trying to open dropdown
    setTimeout(() => {
      // Custom event to communicate with navbar component
      const event = new CustomEvent('openNavDropdown', {
        detail: {
          section: 'wealthManagement', // The Services section
          subsection: 0  // Index of "Investment Advisory" in the items array
        }
      });
      window.dispatchEvent(event);
    }, 100);
  };



  return (
    <div className="relative bg-gradient-to-br from-[#113262] to-[#113262] isolate min-h-[75vh] flex items-center">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 lg:py-24">
        <div className="flex flex-col md:flex-row items-center justify-between">
          {/* Left content */}
          <div className="w-full md:w-1/2 lg:w-3/5 text-white space-y-5 md:space-y-6 md:pr-8 pt-8 md:pt-4">
            <button
              onClick={handleBackClick}
              className="flex items-center gap-2 text-orange-400 hover:text-orange-600 transition-all duration-300 font-medium text-sm mb-6 opacity-60 hover:opacity-100"
            >
              <ArrowLeft className="w-4 h-4 transition-transform duration-300 hover:translate-x-0.5" />
              Back to Wealth Management
            </button>


            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4 leading-tight bg-gradient-to-r from-white to-[#A2B8D8] bg-clip-text text-transparent animate-slideUp py-2">
              Tax Planning
            </h1>

            <p className="text-lg md:text-xl text-[#A2B8D8] animate-fadeIn opacity-0" style={{ animationDelay: '0.3s' }}>
              Maximize Your Wealth with Expert Tax Strategies
            </p>

            <p className="text-[#E8EEF6] text-sm md:text-base leading-relaxed animate-fadeIn opacity-0" style={{ animationDelay: '0.6s' }}>
              Optimize your wealth through intelligent tax planning strategies, designed for high net worth individuals seeking long-term financial success.
            </p>

            {/* Button container in a row */}
            <div className="flex flex-row gap-4 animate-fadeIn opacity-0 mt-6 md:mt-8" style={{ animationDelay: '0.9s' }}>
              <a
                href="https://club.maxiomwealth.com/login"
                className="bg-[#F49611] text-white px-8 py-4 rounded-lg hover:transform hover:-translate-y-1 hover:shadow-lg transition-all duration-300 font-semibold"
              >
                Get Started
              </a>
              <a href="/meeting"
                className="border-2 border-[#A2B8D8] text-white px-5 py-2.5 md:px-6 md:py-3 rounded-lg hover:bg-white/10 transition-colors duration-300 flex items-center gap-2 font-semibold text-sm"
              >
                <PhoneCall size={18} className="shrink-0" />
                Schedule Meeting
              </a>
            </div>
          </div>

          {/* Right image - reduced vertical spacing on mobile */}
          <div className="w-full md:w-1/2 lg:w-2/5 pt-8 md:pt-20 mt-6 md:mt-12 md:mt-0 flex justify-center md:justify-end animate-fadeIn opacity-0" style={{ animationDelay: '1.2s' }}>
            <div className="relative w-full max-w-md px-4 md:px-0">
              <div className="absolute inset-0 rounded-full"></div>
              <img
                src={dummy}
                alt="Spark PMS visualization"
                className="relative z-10 w-full h-auto rounded-lg object-contain max-w-full scale-120"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Services Section
const ServicesSection = () => {
  const [activeService, setActiveService] = useState(null);

  const services = [
    {
      id: 1,
      icon: TrendingUp,
      title: "Tax-Efficient Investment Strategies",
      description: "Optimize your portfolio with tax-advantaged investment approaches",
      details: [
        "ELSS and tax-saving instruments",
        "Strategic asset allocation",
        "Tax loss harvesting opportunities"
      ]
    },
    {
      id: 2,
      icon: BarChart2,
      title: "Portfolio Structuring",
      description: "Structure your investments for optimal tax efficiency",
      details: [
        "Asset location optimization",
        "Tax-efficient rebalancing",
        "Strategic withdrawal planning"
      ]
    },
    {
      id: 3,
      icon: FileText,
      title: "Tax Planning & Compliance",
      description: "Comprehensive tax planning and regulatory compliance",
      details: [
        "Regular tax assessments",
        "Compliance monitoring",
        "Tax regulation updates"
      ]
    }
  ];

  return (
    <div className="bg-white">
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8 pt-12">
        <div className="text-center mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4">Our Tax Planning Services</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Comprehensive tax planning solutions designed to optimize your wealth and achieve your financial goals.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service) => (
            <div
              key={service.id}
              className={`bg-white rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl ${activeService === service.id ? 'ring-2 ring-[#1C52A0]' : ''
                }`}
              onClick={() => setActiveService(activeService === service.id ? null : service.id)}
            >
              <div className="p-6">
                <div className="w-12 h-12 bg-[#E8EEF6] rounded-lg flex items-center justify-center">
                  <service.icon className="h-6 w-6 text-[#1C52A0]" />
                </div>
                <h3 className="mt-4 text-xl font-semibold text-[#113262]">{service.title}</h3>
                <p className="mt-2 text-gray-600">{service.description}</p>

                <div className={`mt-4 transition-all duration-300 ${activeService === service.id ? 'max-h-48' : 'max-h-0 overflow-hidden'
                  }`}>
                  <ul className="space-y-2">
                    {service.details.map((detail, index) => (
                      <li key={index} className="flex items-center text-gray-600">
                        <div className="w-1.5 h-1.5 bg-[#F49611] rounded-full mr-2"></div>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Approach Section
const ApproachSection = () => {
  const steps = [
    {
      number: "01",
      title: "Assessment",
      description: "Comprehensive evaluation of your current tax situation and financial goals"
    },
    {
      number: "02",
      title: "Strategy Development",
      description: "Creating customized tax planning strategies aligned with your objectives"
    },
    {
      number: "03",
      title: "Implementation",
      description: "Executing tax-efficient investment and planning strategies"
    },
    {
      number: "04",
      title: "Monitoring",
      description: "Continuous oversight and adjustments to optimize tax efficiency"
    }
  ];

  return (
    <div className="bg-[#F8FAFC] pt-12 pb-10">
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
        <div className="text-center mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4">Our Approach</h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            A systematic approach to tax planning that ensures comprehensive coverage and optimal results.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              <div className="bg-white rounded-lg p-6 shadow-lg h-full relative z-10">
                <span className="text-4xl font-bold text-[#F49611] opacity-50">{step.number}</span>
                <h3 className="text-xl font-semibold text-[#113262] mt-4">{step.title}</h3>
                <p className="mt-2 text-gray-600">{step.description}</p>
              </div>
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 left-[calc(100%-1rem)] w-[calc(2rem+2rem)] h-[2px] bg-gradient-to-r from-[#1C52A0] to-[#0A1E3A] transform -translate-y-1/2 z-0"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export { TaxPlanningPage, HeroSection, ServicesSection, ApproachSection };